import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CHAIN_NAME } from '@/utils/chain';
import {
  QrCode,
  Scan,
  Plus,
  Shield,
  Clock,
  AlertCircle,
  X,
} from 'lucide-react';
import { CreateQRFlow } from './qr/CreateQRFlow';
import { ScanQRFlow } from './qr/ScanQRFlow';
import { EmergencyQR } from './qr/EmergencyQR';
import { AccessHistory } from './qr/AccessHistory';
import { copyToClipboard } from '../utils/clipboard';

interface QRHubProps {
  onClose: () => void;
  petName: string;
  petId: string;
}

interface ActiveQRCode {
  id: string;
  petName: string;
  accessType: string;
  dataScope: string[];
  expiresAt: string;
  expiryType: string;
  scannedCount: number;
  createdAt: string;
  status: 'active' | 'expired' | 'revoked';
}

export function QRHub({ onClose, petName, petId }: QRHubProps) {
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState<
    'hub' | 'create' | 'scan' | 'emergency' | 'history'
  >('hub');
  const [viewQRModal, setViewQRModal] = useState<string | null>(null);
  const [shareQRModal, setShareQRModal] = useState<string | null>(null);

  const [activeQRs, setActiveQRs] = useState<ActiveQRCode[]>([]);

  const handleRevokeQR = (qrId: string) => {
    setActiveQRs(
      activeQRs.map((qr) =>
        qr.id === qrId ? { ...qr, status: 'revoked' as const } : qr
      )
    );
  };

  const handleQRCreated = (newQR: ActiveQRCode) => {
    setActiveQRs([newQR, ...activeQRs]);
    setActiveView('hub');
  };

  if (activeView === 'create') {
    return (
      <CreateQRFlow
        onClose={() => setActiveView('hub')}
        onComplete={handleQRCreated}
      />
    );
  }

  if (activeView === 'scan') {
    return <ScanQRFlow onClose={() => setActiveView('hub')} />;
  }

  if (activeView === 'emergency') {
    return (
      <EmergencyQR
        petName={petName}
        petId={petId}
        onClose={() => setActiveView('hub')}
      />
    );
  }

  if (activeView === 'history') {
    return <AccessHistory petId={petId} onClose={() => setActiveView('hub')} />;
  }

  return (
    <div
      className="fixed inset-0 flex items-start sm:items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      <div
        className="bg-white dark:bg-[#16151D] rounded-xl max-w-5xl w-full max-w-[calc(100vw-1.5rem)] min-w-0 max-h-[calc(100vh-1.5rem)] overflow-hidden flex flex-col border dark:border-white/8 mt-0 sm:mt-auto sm:my-auto"
        style={{
          boxShadow:
            '0 25px 50px -12px rgba(0,0,0,0.25), 0 12px 24px -8px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.08), 0 0 40px -10px rgba(99,102,241,0.2)',
        }}
      >
        {/* Header - responsive padding and wrapping title */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 sm:p-6 flex-shrink-0">
          <div className="flex justify-between items-start gap-3 mb-4">
            <div className="min-w-0">
              <h2
                className="text-xl sm:text-2xl mb-1"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('features.qrHub.title', 'Pet Access & Sharing')}
              </h2>
              <p
                className="text-sm opacity-90"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t(
                  'features.qrHub.subtitle',
                  `Secure QR-based access control powered by ${CHAIN_NAME}`,
                  { chain: CHAIN_NAME }
                )}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors flex-shrink-0 p-1 -m-1"
              aria-label={t('common.close', 'Close')}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Quick Actions - 2x2 on mobile, 4 in a row on md+ */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
            <button
              onClick={() => setActiveView('create')}
              className="bg-white/20 hover:bg-white/30 rounded-lg p-4 transition-colors text-left cursor-pointer"
            >
              <Plus className="w-6 h-6 mb-2" />
              <div
                className="text-sm"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                {t('features.qrHub.createQR', 'Create QR')}
              </div>
            </button>
            <button
              onClick={() => setActiveView('scan')}
              className="bg-white/20 hover:bg-white/30 rounded-lg p-4 transition-colors text-left cursor-pointer"
            >
              <Scan className="w-6 h-6 mb-2" />
              <div
                className="text-sm"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                {t('features.qrHub.scanQR', 'Scan QR')}
              </div>
            </button>
            <button
              onClick={() => setActiveView('emergency')}
              className="bg-red-500/30 hover:bg-red-500/40 rounded-lg p-4 transition-colors text-left border-2 border-red-300 cursor-pointer"
            >
              <AlertCircle className="w-6 h-6 mb-2" />
              <div
                className="text-sm"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                {t('features.qrHub.emergencyQR.title')}
              </div>
            </button>
            <button
              onClick={() => setActiveView('history')}
              className="bg-white/20 hover:bg-white/30 rounded-lg p-4 transition-colors text-left cursor-pointer"
            >
              <Clock className="w-6 h-6 mb-2" />
              <div
                className="text-sm"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                {t('features.qrHub.accessHistory', 'Access History')}
              </div>
            </button>
          </div>
        </div>

        {/* Content - responsive padding and scrollable */}
        <div className="flex-1 overflow-auto p-4 sm:p-6 min-h-0">
          {/* Stats - 2x2 on mobile */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-4">
              <div
                className="text-sm text-gray-600 dark:text-white/50 mb-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.qrHub.activeQRCodes', 'Active QR Codes')}
              </div>
              <div
                className="text-2xl text-blue-600 dark:text-blue-400"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {activeQRs.filter((qr) => qr.status === 'active').length}
              </div>
            </div>
            <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-4">
              <div
                className="text-sm text-gray-600 dark:text-white/50 mb-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.qrHub.totalScans', 'Total Scans')}
              </div>
              <div
                className="text-2xl text-green-600 dark:text-green-400"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {activeQRs.reduce((sum, qr) => sum + qr.scannedCount, 0)}
              </div>
            </div>
            <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-4">
              <div
                className="text-sm text-gray-600 dark:text-white/50 mb-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.qrHub.expired', 'Expired')}
              </div>
              <div
                className="text-2xl text-gray-400 dark:text-white/30"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {activeQRs.filter((qr) => qr.status === 'expired').length}
              </div>
            </div>
            <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-4">
              <div
                className="text-sm text-gray-600 dark:text-white/50 mb-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.qrHub.revoked', 'Revoked')}
              </div>
              <div
                className="text-2xl text-red-600 dark:text-red-400"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {activeQRs.filter((qr) => qr.status === 'revoked').length}
              </div>
            </div>
          </div>

          {/* Active QR Codes */}
          <div className="mb-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
              <h3
                className="text-lg sm:text-xl text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('features.qrHub.activeQRCodes', 'Active QR Codes')}
              </h3>
              <button
                onClick={() => setActiveView('create')}
                className="flex items-center justify-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors w-full sm:w-auto flex-shrink-0 cursor-pointer"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                <Plus className="w-4 h-4" />
                {t('features.qrHub.createNewQR', 'Create New QR')}
              </button>
            </div>

            <div className="space-y-4">
              {activeQRs
                .filter((qr) => qr.status === 'active')
                .map((qr) => {
                  const expiryDate = new Date(qr.expiresAt);
                  const now = new Date();
                  const hoursRemaining = Math.floor(
                    (expiryDate.getTime() - now.getTime()) / (60 * 60 * 1000)
                  );
                  const isExpiringSoon =
                    hoursRemaining <= 24 && hoursRemaining > 0;

                  return (
                    <div
                      key={qr.id}
                      className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-6"
                    >
                      <div className="flex gap-6">
                        {/* QR Code Preview */}
                        <div className="flex-shrink-0">
                          <div className="w-32 h-32 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-lg flex items-center justify-center">
                            <QrCode className="w-20 h-20 text-indigo-600 dark:text-indigo-400" />
                          </div>
                          <div
                            className="text-xs text-center text-gray-500 dark:text-white/50 mt-2"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            {qr.scannedCount} scan
                            {qr.scannedCount !== 1 ? 's' : ''}
                          </div>
                        </div>

                        {/* QR Details */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h4
                                  className="text-gray-900 dark:text-white"
                                  style={{
                                    fontFamily: 'Sora, sans-serif',
                                    fontWeight: 700,
                                  }}
                                >
                                  {qr.petName}
                                </h4>
                                <span
                                  className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded text-sm"
                                  style={{ fontFamily: 'Inter, sans-serif' }}
                                >
                                  {qr.accessType}
                                </span>
                              </div>
                              <div
                                className="text-sm text-gray-600 dark:text-white/50"
                                style={{ fontFamily: 'Inter, sans-serif' }}
                              >
                                Created:{' '}
                                {new Date(qr.createdAt).toLocaleString()}
                              </div>
                            </div>
                            {isExpiringSoon && (
                              <span
                                className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400 text-sm"
                                style={{ fontFamily: 'Inter, sans-serif' }}
                              >
                                <Clock className="w-4 h-4" />
                                Expires in {hoursRemaining}h
                              </span>
                            )}
                          </div>

                          <div className="mb-3">
                            <div
                              className="text-sm text-gray-600 dark:text-white/50 mb-1"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                            >
                              Data Access
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {qr.dataScope.map((scope, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-white/70 rounded text-xs"
                                  style={{ fontFamily: 'Inter, sans-serif' }}
                                >
                                  {scope}
                                </span>
                              ))}
                            </div>
                          </div>

                          <div
                            className="flex items-center gap-4 text-sm text-gray-600 dark:text-white/50"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            <div>Expires: {expiryDate.toLocaleString()}</div>
                            <div className="capitalize">({qr.expiryType})</div>
                          </div>

                          <div className="flex gap-2 mt-4">
                            <button
                              onClick={() => setViewQRModal(qr.id)}
                              className="px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-sm text-gray-700 dark:text-white/90 cursor-pointer"
                              style={{
                                fontFamily: 'Sora, sans-serif',
                                fontWeight: 700,
                              }}
                            >
                              View QR
                            </button>
                            <button
                              onClick={() => setShareQRModal(qr.id)}
                              className="px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-sm text-gray-700 dark:text-white/90 cursor-pointer"
                              style={{
                                fontFamily: 'Sora, sans-serif',
                                fontWeight: 700,
                              }}
                            >
                              Share
                            </button>
                            <button
                              onClick={() => handleRevokeQR(qr.id)}
                              className="px-4 py-2 border border-red-300 dark:border-red-500/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm cursor-pointer"
                              style={{
                                fontFamily: 'Sora, sans-serif',
                                fontWeight: 700,
                              }}
                            >
                              Revoke Access
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>

            {activeQRs.filter((qr) => qr.status === 'active').length === 0 && (
              <div className="text-center py-12 bg-gray-50 dark:bg-[#0F0E16] rounded-lg border dark:border-white/8">
                <QrCode className="w-16 h-16 text-gray-300 dark:text-white/20 mx-auto mb-4" />
                <p
                  className="text-gray-500 dark:text-white/70 mb-4"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('features.qrHub.noActiveQRCodes', 'No active QR codes')}
                </p>
                <button
                  onClick={() => setActiveView('create')}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors cursor-pointer"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t(
                    'features.qrHub.createYourFirstQRCode',
                    'Create Your First QR Code'
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Expired/Revoked QRs */}
          {activeQRs.filter((qr) => qr.status !== 'active').length > 0 && (
            <div>
              <h3
                className="text-xl mb-4 text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('features.qrHub.inactiveQRCodes', 'Inactive QR Codes')}
              </h3>
              <div className="space-y-3">
                {activeQRs
                  .filter((qr) => qr.status !== 'active')
                  .map((qr) => (
                    <div
                      key={qr.id}
                      className="bg-gray-50 dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-4 opacity-60"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span
                              className="text-gray-900 dark:text-white"
                              style={{
                                fontFamily: 'Sora, sans-serif',
                                fontWeight: 600,
                              }}
                            >
                              {qr.petName}
                            </span>
                            <span
                              className="text-sm text-gray-500 dark:text-white/50"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                            >
                              • {qr.accessType}
                            </span>
                          </div>
                          <div
                            className="text-sm text-gray-600 dark:text-white/50"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            {qr.status === 'expired'
                              ? t('features.qrHub.expired', 'Expired')
                              : t('features.qrHub.revoked', 'Revoked')}
                            : {new Date(qr.expiresAt).toLocaleString()}
                          </div>
                        </div>
                        <span
                          className={`px-3 py-1 rounded-full text-sm ${
                            qr.status === 'expired'
                              ? 'bg-gray-200 dark:bg-white/10 text-gray-700 dark:text-white/70'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                          }`}
                          style={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                          }}
                        >
                          {qr.status === 'expired'
                            ? t('features.qrHub.expired', 'Expired')
                            : t('features.qrHub.revoked', 'Revoked')}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div>
                <h4
                  className="mb-2 text-blue-900 dark:text-blue-100"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t(
                    'features.qrHub.blockchainTitle',
                    'Blockchain-Secured Access Control'
                  )}
                </h4>
                <p
                  className="text-sm text-gray-700 dark:text-white/70 mb-3"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('features.qrHub.blockchainDesc')}
                </p>
                <ul
                  className="text-sm text-gray-700 dark:text-white/70 space-y-1"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <li>
                    ✓{' '}
                    {t(
                      'features.qrHub.realTimeNotifications',
                      'Real-time access notifications'
                    )}
                  </li>
                  <li>
                    ✓{' '}
                    {t(
                      'features.qrHub.instantRevocation',
                      'Instant revocation capability'
                    )}
                  </li>
                  <li>
                    ✓{' '}
                    {t(
                      'features.qrHub.completeAuditTrail',
                      'Complete audit trail'
                    )}
                  </li>
                  <li>
                    ✓{' '}
                    {t(
                      'features.qrHub.encryptedTransmission',
                      'Encrypted data transmission'
                    )}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View QR Modal */}
      {viewQRModal &&
        (() => {
          const qr = activeQRs.find((q) => q.id === viewQRModal);
          if (!qr) return null;

          return (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
              <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-md w-full p-6 border dark:border-white/8">
                <div className="flex justify-between items-center mb-6">
                  <h3
                    className="text-xl text-gray-900 dark:text-white"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {t('features.qrHub.qrCodeLabel', 'QR Code')}
                  </h3>
                  <button
                    onClick={() => setViewQRModal(null)}
                    className="text-gray-500 dark:text-white/50 hover:text-gray-700 dark:hover:text-white/70"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-xl p-8 mb-6">
                  <div className="bg-white dark:bg-[#0F0E16] rounded-lg p-4">
                    <QrCode className="w-full h-64 text-indigo-600 dark:text-indigo-400" />
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-[#0F0E16] rounded-lg p-4 mb-4 border dark:border-white/8">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span
                        className="text-gray-600 dark:text-white/50"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {t('features.qrHub.petLabel', 'Pet:')}
                      </span>
                      <span
                        className="text-gray-900 dark:text-white"
                        style={{
                          fontFamily: 'Sora, sans-serif',
                          fontWeight: 600,
                        }}
                      >
                        {qr.petName}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span
                        className="text-gray-600 dark:text-white/50"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {t('features.qrHub.accessTypeLabel', 'Access Type:')}
                      </span>
                      <span
                        className="text-gray-900 dark:text-white"
                        style={{
                          fontFamily: 'Sora, sans-serif',
                          fontWeight: 600,
                        }}
                      >
                        {qr.accessType}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span
                        className="text-gray-600 dark:text-white/50"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {t('features.qrHub.expiresLabel', 'Expires:')}
                      </span>
                      <span
                        className="text-gray-900 dark:text-white"
                        style={{
                          fontFamily: 'Sora, sans-serif',
                          fontWeight: 600,
                        }}
                      >
                        {new Date(qr.expiresAt).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span
                        className="text-gray-600 dark:text-white/50"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {t('features.qrHub.totalScans', 'Scans')}:
                      </span>
                      <span
                        className="text-gray-900 dark:text-white"
                        style={{
                          fontFamily: 'Sora, sans-serif',
                          fontWeight: 600,
                        }}
                      >
                        {qr.scannedCount}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      // Download QR logic
                      alert(t('features.qrHub.downloadStarted'));
                    }}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {t('common.download', 'Download')}
                  </button>
                  <button
                    onClick={() => {
                      // Print QR logic
                      window.print();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-white/90"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {t('common.print', 'Print')}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Share QR Modal */}
      {shareQRModal &&
        (() => {
          const qr = activeQRs.find((q) => q.id === shareQRModal);
          if (!qr) return null;

          const shareLink = `https://petso.io/qr/${qr.id}`;

          return (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
              <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-lg w-full p-6 border dark:border-white/8">
                <div className="flex justify-between items-center mb-6">
                  <h3
                    className="text-xl text-gray-900 dark:text-white"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {t('features.qrHub.shareQRAccess', 'Share QR Access')}
                  </h3>
                  <button
                    onClick={() => setShareQRModal(null)}
                    className="text-gray-500 dark:text-white/50 hover:text-gray-700 dark:hover:text-white/70"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="mb-6">
                  <div
                    className="text-sm text-gray-600 dark:text-white/50 mb-2"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('features.qrHub.shareLinkLabel', 'Share Link')}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={shareLink}
                      readOnly
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg bg-gray-50 dark:bg-[#0F0E16] text-sm text-gray-900 dark:text-white"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    />
                    <button
                      onClick={() => {
                        copyToClipboard(shareLink);
                        alert(
                          t(
                            'features.qrHub.linkCopied',
                            'Link copied to clipboard!'
                          )
                        );
                      }}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      {t('common.copy', 'Copy')}
                    </button>
                  </div>
                </div>

                <div className="mb-6">
                  <div
                    className="text-sm text-gray-600 dark:text-white/50 mb-3"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('features.qrHub.shareVia', 'Share via')}
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <button
                      onClick={() =>
                        window.open(
                          `mailto:?subject=Pet Access - ${qr.petName}&body=Access link: ${shareLink}`,
                          '_blank'
                        )
                      }
                      className="p-4 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 text-center"
                    >
                      <div className="text-2xl mb-1">📧</div>
                      <div
                        className="text-xs text-gray-700 dark:text-white/70"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {t('features.qrHub.email', 'Email')}
                      </div>
                    </button>
                    <button
                      onClick={() =>
                        window.open(
                          `sms:?body=Access link for ${qr.petName}: ${shareLink}`,
                          '_blank'
                        )
                      }
                      className="p-4 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 text-center"
                    >
                      <div className="text-2xl mb-1">💬</div>
                      <div
                        className="text-xs text-gray-700 dark:text-white/70"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {t('features.qrHub.sms', 'SMS')}
                      </div>
                    </button>
                    <button
                      onClick={() =>
                        window.open(
                          `https://wa.me/?text=Access link for ${qr.petName}: ${shareLink}`,
                          '_blank'
                        )
                      }
                      className="p-4 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 text-center"
                    >
                      <div className="text-2xl mb-1">📱</div>
                      <div
                        className="text-xs text-gray-700 dark:text-white/70"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        WhatsApp
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        // Download QR as image
                        alert(t('features.qrHub.downloadImageStarted'));
                      }}
                      className="p-4 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 text-center"
                    >
                      <div className="text-2xl mb-1">⬇️</div>
                      <div
                        className="text-xs text-gray-700 dark:text-white/70"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {t('common.download', 'Download')}
                      </div>
                    </button>
                  </div>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-500/30 rounded-lg p-4">
                  <p
                    className="text-sm text-yellow-800 dark:text-yellow-200"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('features.qrHub.shareWarning', { petName: qr.petName })}
                  </p>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
}
